import { WebhookSearch } from "../../../types/domain";
import {
  DataTable,
  TableColumn,
  LinkCell,
  ActionCell,
  TextCell,
} from "@/components/common/Table";
import { useNavigate } from "react-router";
import { faPencil } from "@fortawesome/free-solid-svg-icons";
import { DeleteWebhookDialog } from "./DeleteWebhookDialog";
import { Alert } from "@/components/common/Alert/Alert";
import { LoadingPage } from "@/components/common/Loading/LoadingPage";
import { Badge } from "@/components/shadcn/badge";
import { WebhookEmptyStateCard } from "./WebhookEmptyStateCard";

type WebhookListProps = {
  webhooks: WebhookSearch[];
  onWebhookDeleted?: () => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
};

export const WebhookList = ({
  webhooks,
  onWebhookDeleted,
  isLoading = false,
  error,
}: WebhookListProps) => {
  const navigate = useNavigate();

  const columns: TableColumn<WebhookSearch>[] = [
    {
      key: "Name",
      header: "Name",
      render: (webhook: WebhookSearch) => (
        <LinkCell
          onClick={() => navigate(`/maintenance/webhook/${webhook.id}`)}
          text={webhook.name}
          title="Webhook Details"
        />
      ),
    },
    {
      key: "Url",
      header: "Url",
      render: (webhook: WebhookSearch) => (
        <LinkCell
          onClick={() => navigate(`/maintenance/webhook/${webhook.id}`)}
          text={webhook.url || "Unnamed Webhook"}
          title="Webhook Details"
        />
      ),
    },
    {
      key: "active",
      header: "Active",
      skeleton: { variant: "long" },
      render: (webhook: WebhookSearch) => (
        <Badge variant={webhook.isActive ? "success" : "secondary"}>
          {webhook.isActive ? "Enabled" : "Inactive"}
        </Badge>
      ),
    },
    {
      key: "subscribedEvents",
      header: "Subscribed Events",
      skeleton: { variant: "short" },
      render: (webhook: WebhookSearch) => (
        <TextCell value={webhook.eventCount.toString()} />
      ),
    },
    {
      key: "actions",
      header: "",
      isAction: true,
      skeleton: { variant: "short" },
      render: (webhook: WebhookSearch) => (
        <div className="flex items-center gap-1">
          <ActionCell
            actions={[
              {
                icon: faPencil,
                title: "Edit",
                onClick: () =>
                  navigate(`/maintenance/webhook/edit/${webhook.id}`),
              },
            ]}
          />
          {onWebhookDeleted && (
            <DeleteWebhookDialog
              webhook={webhook}
              callBack={onWebhookDeleted}
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      {error && (
        <Alert variant="error" persistent={true}>
          {error}
        </Alert>
      )}
      {isLoading ? (
        <LoadingPage message="Loading webhooks..." />
      ) : webhooks && webhooks.length === 0 ? (
        <WebhookEmptyStateCard />
      ) : (
        <DataTable<WebhookSearch>
          columns={columns}
          data={webhooks}
          isLoading={isLoading}
          skeletonRows={10}
        />
      )}
    </div>
  );
};
